"use client"

import { useQuery, UseQueryOptions } from "@tanstack/react-query"
import { useConfig, useWatchBlockNumber } from "wagmi"
import { getPublicClient } from "wagmi/actions"
import { decodeEventLog } from "viem"
import { useCallback, useMemo } from "react"
/**
 * Fetches events from the blockchain
 *
 * This exists and is implemented as SWR because wagmi's `useWatchContractEvent` doesn't do anything, `onLogs` never gets called for
 * new realtime events, so we're using a trusted solution to manage data revalidation ourselves.
 *
 * @param address
 * @param abi
 * @param eventName
 * @param chainId
 */
export const useContractEvents = ({
  address,
  abi,
  eventName,
  chainId,
  args,
  // TODO: use more sensible default value, no need to read events from 2011
  fromBlock = 0n,
  enabled,
  ...useQueryOptions
}: {
  address: `0x${string}`
  abi: any
  eventName: string
  chainId: number
  args?: any
  fromBlock?: bigint
  enabled?: boolean
  useQueryOptions?: UseQueryOptions
}) => {
  const config = useConfig()
  const client = getPublicClient(config, { chainId })
  const event = abi.find((e) => e.name === eventName && e.type === "event")
  const queryKey = useMemo(
    () => [address, chainId, eventName, fromBlock, args],
    [address, chainId, eventName, fromBlock, args],
  )

  const query = useQuery({
    enabled,
    queryKey,
    queryFn: useCallback<any>(async () => {
      const getLogs = (fromBlock_: bigint, toBlock_?: bigint) =>
        client!.getLogs({
          fromBlock: fromBlock_,
          toBlock: toBlock_,
          address,
          event,
          args,
        })

      try {
        const logs = await getLogs(fromBlock)
        return logs
      } catch (e: any) {
        console.log("Error fetching logs", { e })
        if (e.name === "InvalidParamsRpcError") {
          // Then it's likely an issue with reponse size being > 10k events
          console.log("Falling back to smaller batches")
          const currentBlock = await client!.getBlockNumber()
          // Loop through in batches of 2000, using simulataneous promises
          const batch = 2001n
          const promises = [] as any[]
          for (let i = fromBlock; i < currentBlock; i += batch) {
            promises.push(getLogs(i, i + batch))
          }
          // Concat all the logs
          return (await Promise.all(promises)).flat()
        }
      }
      return null
    }, [client, address, event, args, fromBlock]),
    select: (data: any) => {
      const parsed = data.map((log: any) => ({
        log,
        ...decodeEventLog({
          abi,
          ...log,
        }),
      }))
      return parsed
    },

    ...useQueryOptions,
  })

  const refetch = query.refetch
  // watchContractEvent seems to be very unreliable, throwing RPC errors
  // and being called too many times. This isn't great as it seems to be one block behind
  // so the UI feels laggy when the UI reacts on an event.
  useWatchBlockNumber({
    chainId,
    onBlockNumber: () => setTimeout(() => enabled && refetch(), 100),
  })

  // this is returning an error, perhaps because of the provider but i'm not sure..
  // https://github.com/wevm/viem/issues/1063#issuecomment-1925459220
  // useWatchContractEvent({
  //   address,
  //   abi,
  //   eventName,
  //   chainId,
  //   enabled,
  //   args,
  //   fromBlock,
  //   onError: useCallback((error) => {
  //     console.log("onError", { error })
  //   }, []),
  //   onLogs: useCallback(() => refetch(), [refetch]),
  // })

  return query
}
